import { ReactNode, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { SupportChatWidget } from "./SupportChatWidget";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { PWAUpdateBanner } from "@/components/PWAUpdateBanner";
// Reports when a member's browser is running a build the server has already replaced.
import { checkForStaleClient } from "@/lib/staleClientWatch";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { ProfileSwitchBanner } from "@/components/ProfileSwitchBanner";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useProfileSwitch } from "@/contexts/ProfileSwitchContext";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";

interface DashboardLayoutProps {
  children: ReactNode;
  noPadding?: boolean;
  hideHamburger?: boolean;
}

export function DashboardLayout({ children, noPadding, hideHamburger }: DashboardLayoutProps) {
  // Enable global keyboard shortcuts
  useKeyboardShortcuts({ enabled: true });
  const { isImpersonating } = useImpersonation();
  // Both banners are `fixed top-0`, and the header is only `sticky top-0 z-30` — so without
  // this padding the banner sits ON the header and swallows clicks meant for the avatar.
  // Found by the e2e: "banner intercepts pointer events" when it tried to open the profile
  // menu while switched, which is the one place the way back is supposed to live.
  const { isSwitched } = useProfileSwitch();
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebar-collapsed");
    return saved === "true";
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Listen for sidebar collapse changes
  useEffect(() => {
    const handleStorageChange = () => {
      const collapsed = localStorage.getItem("sidebar-collapsed") === "true";
      setSidebarCollapsed(collapsed);
    };

    // Check periodically for changes (since storage event only fires cross-tab)
    const interval = setInterval(handleStorageChange, 100);
    return () => clearInterval(interval);
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Kept deliberately (VA-270). This produced the single row that identified the white
  // screen as a stuck service worker rather than a caching bug, after three fixes aimed at
  // the wrong thing. It is also the only way to answer "did the fix actually reach them",
  // which is the question that let this run for a month.
  //
  // Five seconds in, so it never competes with the first paint or the member's first action.
  useEffect(() => {
    const t = setTimeout(() => { void checkForStaleClient(); }, 5000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={cn("min-h-screen bg-background overflow-x-clip", (isImpersonating || isSwitched) && "pt-10")}>
      {/* Impersonation banner */}
      <ImpersonationBanner />
      <ProfileSwitchBanner />

      {/* Skip to main content link - visible on focus for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-20 focus:z-[60] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* PWA indicators */}
      <OfflineIndicator />
      <PWAUpdateBanner />

      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile hamburger button */}
      {!hideHamburger && (
        <Button
          variant="outline"
          size="icon"
          className="fixed top-4 left-4 z-50 lg:hidden bg-card border-border text-foreground hover:bg-muted shadow-md min-h-[44px] min-w-[44px]"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      )}

      {/* Sidebar - hidden on mobile, shown on desktop */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-40 transition-transform duration-300",
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        "lg:translate-x-0"
      )}>
        <Sidebar />
      </div>

      {/* Main content area */}
      <div className={cn(
        "min-h-screen transition-all duration-300",
        sidebarCollapsed ? "lg:pl-16" : "lg:pl-60"
      )}>
        <Header />
        <main id="main-content" className={noPadding ? "overflow-hidden" : "p-4 md:p-6 lg:p-8 pb-safe max-w-7xl mx-auto overflow-x-clip"}>
          {children}
        </main>
      </div>

      {/* Support chat widget */}
      <SupportChatWidget />
    </div>
  );
}
